import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  createRevisionAsync,
  getRevisionAsync,
  updateRevisionAsync
} from './revisionSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    }
  })
);

interface CreateEditRevision {
  id: number | null;
  name: string | null;
}

export default function RevisionCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedRevision } = useSelector(
    (state: RootState) => state.revisions
  );

  const params = useParams<{ revisionId: string }>();
  const revisionId = Number.parseInt(params.revisionId, 10);
  const isNewPage = !Boolean(revisionId);

  const [revision, setRevision] = useState<CreateEditRevision>({
    id: null,
    name: null
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedRevision) {
      setRevision({
        ...revision,
        id: selectedRevision.id,
        name: selectedRevision.name
      });
    }
  }, [selectedRevision]);

  useEffect(() => {
    if (!isNewPage) dispatch(getRevisionAsync(revisionId));
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setRevision({
      ...revision,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (revision.name) {
        if (isNewPage) {
          // create revision
          const createRevision = {
            name: revision.name
          };
          await dispatch(createRevisionAsync(createRevision)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('revisionCreateEdit.messages.createSuccess')
            })
          );
        } else {
          // update revision
          const updateRevision = {
            id: revision.id,
            name: revision.name
          };
          await dispatch(updateRevisionAsync(updateRevision)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('revisionCreateEdit.messages.updateSuccess')
            })
          );
        }
      } else {
        throw new Error(t('revisionCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('masterData.revision.title'),
          masterDataRef: 'revisions',
          masterDataSubName: selectedRevision?.name,
          masterDataSubRef: selectedRevision?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage
          ? t('masterData.revision.newTitle')
          : t('masterData.revision.editTitle')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('masterData.revision.info')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            id="revision-name"
            label={t('masterData.revision.fields.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={revision.name ? revision.name : ''}
            onChange={onChangeField}
            required
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('masterData.revision.add') : t('projectInfoCard.update')}
        </Button>
      </form>
    </div>
  );
}
